import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
type Screen = 'hero' | 'decision1' | 'decision2';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9VHVUjqgppDSdH8H4rjp3H/Ruby_Logo_new2w.svg';
const PHOTO_REAL = 'https://cdn.magicpatterns.com/uploads/mk5MsRfsb8AqzKhrpux61K/6R2A3078_copy.jpg';
const PHOTO_ONLINE = 'https://cdn.magicpatterns.com/uploads/tbqBJ4cqLN7jUjDv9QxuzQ/6R2A1603_copy.jpg';
const PHOTO_HERO = 'https://cdn.magicpatterns.com/uploads/2o9R97xAcsMaipQKoAfCjd/6R2A2981_copy.jpg';
const SHIMMER_STYLES = `
@keyframes shimmer-sweep {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
.rubyversum-shimmer {
  color: #941318;
  background: linear-gradient(
    90deg,
    #941318 0%,
    #941318 35%,
    #d1bc8a 42%,
    #ffffff 50%,
    #d1bc8a 58%,
    #941318 65%,
    #941318 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer-sweep 8s ease-in-out infinite;
}
`;
export function Rubyversum() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('hero');
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Khand:wght@400;500;600;700&family=Roboto+Condensed:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  const cardVariants = {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    hover: {
      scale: 1.02
    }
  };
  return <div className="min-h-screen w-full bg-[#0A0A0A] text-white overflow-hidden" data-id="element-2">
      <style dangerouslySetInnerHTML={{
      __html: SHIMMER_STYLES
    }} data-id="element-3" />
      <AnimatePresence mode="wait" data-id="element-4">
        {currentScreen === 'hero' && <motion.div key="hero" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6" data-id="element-5">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url(${PHOTO_HERO})`
        }} data-id="element-6">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/65 via-[#0A0A0A]/50 to-[#0A0A0A]/75" data-id="element-7" />
            </div>

            {/* Ambient Red Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-8">
              <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#941318]/20 rounded-full blur-[120px]" data-id="element-9" />
              <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#941318]/15 rounded-full blur-[100px]" data-id="element-10" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center text-center max-w-4xl" data-id="element-11">
              <motion.img src={LOGO_URL} alt="Ruby June Logo" className="w-32 md:w-40 mb-8" initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.6
          }} data-id="element-12" />

              <motion.h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-[0.25em] mb-6 rubyversum-shimmer" style={{
            fontFamily: "'Cinzel', serif"
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} data-id="element-13">
                RUBYVERSUM
              </motion.h1>

              <motion.p className="text-xl md:text-2xl text-[#d1bc8a] mb-12 tracking-wide" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} data-id="element-14">
                Willkommen in meinem Universum
              </motion.p>

              <motion.button onClick={() => setCurrentScreen('decision1')} className="px-10 py-4 bg-transparent border-2 border-[#d1bc8a]/60 text-[#d1bc8a] text-lg font-medium tracking-widest uppercase transition-all duration-300 hover:bg-[#d1bc8a]/15 hover:text-[#d1bc8a] hover:shadow-[0_0_30px_rgba(209,188,138,0.2)]" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.6
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} data-id="element-15">
                Eintreten
              </motion.button>
            </div>
          </motion.div>}

        {currentScreen === 'decision1' && <motion.div key="decision1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6 py-12" data-id="element-16">
            {/* Ambient Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-17">
              <div className="absolute top-1/3 left-0 w-72 h-72 bg-[#941318]/20 rounded-full blur-[100px]" data-id="element-18" />
              <div className="absolute bottom-1/3 right-0 w-72 h-72 bg-[#d1bc8a]/10 rounded-full blur-[100px]" data-id="element-19" />
            </div>

            <div className="relative z-10 w-full max-w-5xl" data-id="element-20">
              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl text-center mb-4 text-[#941318]" style={{
            fontFamily: "'Khand', sans-serif"
          }} initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-21">
                Wie möchtest du Ruby June erleben?
              </motion.h2>

              <motion.p className="text-[#d1bc8a]/70 text-center mb-12 text-lg" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3
          }} data-id="element-22">
                Triff deine Wahl
              </motion.p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-23">
                {/* Real Card */}
                <motion.a href="https://ruby-june.de/" target="_blank" rel="noopener noreferrer" className="group relative h-[400px] md:h-[500px] rounded-lg overflow-hidden cursor-pointer" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.4,
              duration: 0.5
            }} data-id="element-24">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_REAL})`
              }} data-id="element-25" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 group-hover:from-black/80 transition-all duration-300" data-id="element-26" />
                  <div className="absolute inset-0 border-2 border-[#941318]/50 rounded-lg group-hover:border-[#941318] group-hover:shadow-[0_0_40px_rgba(148,19,24,0.3)] transition-all duration-300" data-id="element-27" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-28">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#941318] mb-3" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-29">
                      Real erleben
                    </h3>
                    <p className="text-gray-300 text-lg" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-30">
                      Persönliche Sessions in meinem Studio
                    </p>
                    <div className="mt-4 inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-31">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-32">
                        Zur Website
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-33">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-34" />
                      </svg>
                    </div>
                  </div>
                </motion.a>

                {/* Online Card */}
                <motion.button onClick={() => setCurrentScreen('decision2')} className="group relative h-[400px] md:h-[500px] rounded-lg overflow-hidden cursor-pointer text-left" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.5,
              duration: 0.5
            }} data-id="element-35">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_ONLINE})`
              }} data-id="element-36" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 group-hover:from-black/80 transition-all duration-300" data-id="element-37" />
                  <div className="absolute inset-0 border-2 border-[#d1bc8a]/40 rounded-lg group-hover:border-[#d1bc8a]/70 group-hover:shadow-[0_0_40px_rgba(209,188,138,0.25)] transition-all duration-300" data-id="element-38" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-39">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d1bc8a] mb-3" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-40">
                      Online erleben
                    </h3>
                    <p className="text-gray-300 text-lg" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-41">
                      Erziehung, Austausch & exklusive Einblicke
                    </p>
                    <div className="mt-4 inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-42">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-43">
                        Online entdecken
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-44">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-45" />
                      </svg>
                    </div>
                  </div>
                </motion.button>
              </div>
            </div>
          </motion.div>}

        {currentScreen === 'decision2' && <motion.div key="decision2" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6 py-12" data-id="element-46">
            {/* Ambient Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-47">
              <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-[#d1bc8a]/15 rounded-full blur-[100px]" data-id="element-48" />
              <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-[#941318]/15 rounded-full blur-[100px]" data-id="element-49" />
            </div>

            <div className="relative z-10 w-full max-w-5xl" data-id="element-50">
              {/* Back Button */}
              <motion.button onClick={() => setCurrentScreen('decision1')} className="flex items-center text-[#d1bc8a]/60 hover:text-[#d1bc8a] mb-8 transition-colors" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-51">
                <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-52" />
                Zurück
              </motion.button>

              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl text-center mb-4 text-[#941318]" style={{
            fontFamily: "'Khand', sans-serif"
          }} initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-53">
                Wähle deine Plattform
              </motion.h2>

              <motion.p className="text-[#d1bc8a]/70 text-center mb-12 text-lg" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} data-id="element-54">
                Wo möchtest du mich online erleben?
              </motion.p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-55">
                {/* Bestfans Card */}
                <motion.a href="https://www.bestfans.com/ruby-june" target="_blank" rel="noopener noreferrer" className="group relative h-[300px] md:h-[350px] rounded-lg overflow-hidden cursor-pointer bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d]" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.5,
              duration: 0.5
            }} data-id="element-56">
                  <div className="absolute inset-0 border-2 border-[#941318]/50 rounded-lg group-hover:border-[#941318] group-hover:shadow-[0_0_40px_rgba(148,19,24,0.3)] transition-all duration-300" data-id="element-57" />

                  {/* Decorative gradient */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#d1bc8a]/15 to-transparent rounded-bl-full" data-id="element-58" />

                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center" data-id="element-59">
                    <div className="w-16 h-16 mb-6 rounded-full bg-[#d1bc8a]/15 flex items-center justify-center" data-id="element-60">
                      <svg className="w-8 h-8 text-[#d1bc8a]" fill="currentColor" viewBox="0 0 24 24" data-id="element-61">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-62" />
                      </svg>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d1bc8a] mb-3" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-63">
                      Online Erziehung
                    </h3>
                    <p className="text-gray-300 text-lg mb-6" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-64">
                      Dein täglicher Zugang zu meiner Führung mit persönlichen
                      Nachrichten & regelmäßigen Aufgaben.
                    </p>
                    <div className="inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-65">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-66">
                        Bestfans besuchen
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-67">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-68" />
                      </svg>
                    </div>
                  </div>
                </motion.a>

                {/* MyMemberSite Card */}
                <motion.a href="#" onClick={e => e.preventDefault()} className="group relative h-[300px] md:h-[350px] rounded-lg overflow-hidden cursor-not-allowed bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d]" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.6,
              duration: 0.5
            }} data-id="element-69">
                  <div className="absolute inset-0 border-2 border-[#941318]/40 rounded-lg group-hover:border-[#941318]/70 group-hover:shadow-[0_0_40px_rgba(148,19,24,0.25)] transition-all duration-300" data-id="element-70" />

                  {/* Decorative gradient */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#941318]/10 to-transparent rounded-bl-full" data-id="element-71" />

                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center" data-id="element-72">
                    <div className="w-16 h-16 mb-6 rounded-full bg-[#941318]/15 flex items-center justify-center" data-id="element-73">
                      <svg className="w-8 h-8 text-[#941318]" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-74">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" data-id="element-75" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-76" />
                      </svg>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#941318] mb-3" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-77">
                      Mein Videostore
                    </h3>
                    <p className="text-gray-300 text-lg mb-6" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-78">
                      Alle meine BDSM-Videos für deine Fantasie & deinen Fetisch
                      auf einen Blick & sofort verfügbar.
                    </p>
                    <div className="inline-flex items-center text-[#941318] font-medium group-hover:text-[#941318]/80 transition-colors" data-id="element-79">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-80">
                        Videostore besuchen
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-81">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-82" />
                      </svg>
                    </div>
                  </div>
                </motion.a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}